import { createContext, useContext, useEffect, useMemo, useState } from "react";
import type { Session, User } from "@supabase/supabase-js";
import { useTranslation } from "react-i18next";
import { usePostHog } from "posthog-js/react";
import * as Sentry from "@sentry/react";
import { supabase } from "@/lib/supabase";
import { useErrorHandler } from "@/hooks/use-error-handler";
import { pushToDataLayer } from "@/lib/gtm";
import { getStoredAttribution } from "@/lib/attribution";
import { detectCountry, fetchMarketplaceData } from "@/lib/marketplaces";

interface SendOtpData {
  email: string;
}

interface VerifyOtpData {
  email: string;
  token: string;
  type: "email" | "recovery" | "invite" | "email_change";
}

interface SignInWithOAuthData {
  provider: "google" | "apple";
}

interface AuthContextType {
  session: Session | null;
  user: User | null;
  sendOtp: (data: SendOtpData) => Promise<void>;
  signOut: () => Promise<void>;
  verifyOtp: (data: VerifyOtpData) => Promise<void>;
  signInWithOAuth: (data: SignInWithOAuthData) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const { i18n } = useTranslation();
  const posthog = usePostHog();
  const { createHandler } = useErrorHandler();

  const errorHandler = useMemo(
    () =>
      createHandler({
        feature: "auth",
      }),
    [createHandler],
  );

  useEffect(() => {
    // Prevent duplicate welcome emails when SIGNED_IN fires multiple times rapidly
    const welcomeEmailSent = new Set<string>();

    const syncSentryUser = (authUser: User | null) => {
      if (authUser) {
        Sentry.setUser({ id: authUser.id, email: authUser.email });
      } else {
        const distinctId = posthog.get_distinct_id?.();
        Sentry.setUser(distinctId ? { id: distinctId } : null);
      }
    };

    // Get initial session.
    const bootStartedAt = performance.now();
    Sentry.addBreadcrumb({
      category: "boot",
      level: "info",
      message: "auth-provider getSession start",
    });
    supabase.auth.getSession().then(({ data: { session } }) => {
      Sentry.addBreadcrumb({
        category: "boot",
        level: "info",
        message: "auth-provider getSession resolved",
        data: {
          ms: Math.round(performance.now() - bootStartedAt),
          hasSession: !!session,
        },
      });
      setSession(session);
      setUser(session?.user ?? null);
      syncSentryUser(session?.user ?? null);

      // Identify user in PostHog on initial load
      if (session?.user) {
        posthog.identify(session.user.id, {
          email: session.user.email,
          created_at: session.user.created_at,
        });
      }
    });

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      Sentry.addBreadcrumb({
        category: "boot",
        level: "info",
        message: "auth-provider onAuthStateChange",
        data: { event, hasSession: !!session },
      });
      setSession(session);
      setUser(session?.user ?? null);
      syncSentryUser(session?.user ?? null);

      // Handle PostHog identification based on auth event
      if (event === "SIGNED_IN" && session?.user) {
        const userId = session.user.id;

        // First-touch acquisition attribution forwarded from the ad click.
        // Passed as PostHog $set_once and written to the profile only when not
        // already captured, so the original source survives later sign-ins.
        const attribution = getStoredAttribution();

        posthog.identify(
          userId,
          {
            email: session.user.email,
            created_at: session.user.created_at,
          },
          attribution,
        );

        // Always sync device language to profile for email localization
        // This ensures emails go out in the user's current language
        supabase
          .from("profiles")
          .update({ locale: i18n.language })
          .eq("id", userId)
          .then();

        // Country for admin analytics, detected the same way as the
        // marketplace logos (browser timezone first; survives VPNs).
        fetchMarketplaceData()
          .then((data) => {
            const country = detectCountry(data, i18n.language.split("-")[0]);
            if (!country) return;
            supabase
              .from("profiles")
              .update({ country })
              .eq("id", userId)
              .then();
          })
          .catch((err) => {
            errorHandler(err, {
              action: "sync-profile-country",
              severity: "low",
              showToast: false,
            });
          });

        // Persist attribution first-touch: only when we have something to store
        // and the profile has not captured a source yet. Meta cookies (fbp/fbc)
        // are exempt from the first-touch guard — they may appear only after
        // the Pixel loads on a later visit, and CAPI match quality needs them —
        // so missing ones are backfilled on any sign-in.
        if (Object.keys(attribution).length > 0) {
          supabase
            .from("profiles")
            .select("utm_source, fbclid, fbp, fbc")
            .eq("id", userId)
            .single()
            .then(({ data: profile }) => {
              if (!profile) return;
              if (!profile.utm_source && !profile.fbclid) {
                supabase
                  .from("profiles")
                  .update(attribution)
                  .eq("id", userId)
                  .then();
                return;
              }
              const backfill: { fbp?: string; fbc?: string } = {};
              if (!profile.fbp && attribution.fbp)
                backfill.fbp = attribution.fbp;
              if (!profile.fbc && attribution.fbc)
                backfill.fbc = attribution.fbc;
              if (Object.keys(backfill).length > 0) {
                supabase
                  .from("profiles")
                  .update(backfill)
                  .eq("id", userId)
                  .then();
              }
            });
        }

        // Track signup method
        const provider = session.user.app_metadata?.provider ?? "email";
        posthog.identify(session.user.id, { signup_method: provider });

        // Send welcome email after verification (all signup methods)
        if (!welcomeEmailSent.has(session.user.id)) {
          welcomeEmailSent.add(session.user.id);
          supabase
            .from("profiles")
            .select("email_welcome_sent_at")
            .eq("id", session.user.id)
            .single()
            .then(({ data: profile }) => {
              if (profile && !profile.email_welcome_sent_at) {
                posthog.capture("user_signed_up", { method: provider });
                // Upper-funnel conversion for Google Ads / Meta (fires once per
                // genuinely new user via the email_welcome_sent_at gate above).
                pushToDataLayer({ event: "sign_up", method: provider });
                supabase.functions
                  .invoke("send-emails", {
                    body: {
                      emailTypes: ["welcome"],
                      userId: session.user.id,
                    },
                  })
                  .catch((err) => {
                    errorHandler(err, {
                      action: "send-welcome-email",
                      severity: "low",
                      showToast: false,
                    });
                  });
              }
            });
        }
      } else if (event === "SIGNED_OUT") {
        posthog.reset();
      }
    });

    return () => subscription.unsubscribe();
  }, [posthog, i18n.language, errorHandler]);

  const sendOtp = async ({ email }: SendOtpData) => {
    const result = await supabase.auth.signInWithOtp({
      email,
      options: { shouldCreateUser: true },
    });
    if (result.error) throw new Error(result.error.message);
    posthog.capture("otp_requested", { method: "email" });
  };

  const signOut = async () => {
    const result = await supabase.auth.signOut();
    if (result.error) throw new Error(result.error.message);
    posthog.capture("user_signed_out");
  };

  const verifyOtp = async ({ email, token, type }: VerifyOtpData) => {
    const result = await supabase.auth.verifyOtp({ email, token, type });
    if (result.error) throw new Error(result.error.message);
  };

  const signInWithOAuth = async ({ provider }: SignInWithOAuthData) => {
    const result = await supabase.auth.signInWithOAuth({
      provider,
      options: {
        redirectTo: `${window.location.origin}/`,
      },
    });
    if (result.error) throw new Error(result.error.message);
    // OAuth auth events tracked via onAuthStateChange since redirect happens
    posthog.capture("oauth_initiated", { provider });
  };

  const contextValue = {
    session,
    user,
    sendOtp,
    signOut,
    verifyOtp,
    signInWithOAuth,
  };

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
}

export function useAuthContext() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuthContext must be used within an AuthProvider");
  }
  return context;
}
